import React from 'react';
import useScrollReveal from '../../hooks/useScrollReveal';
import './CoreFeatures.css';

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
    <rect x="3" y="4" width="18" height="18"/><line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const DoctorIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
    <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
  </svg>
);

const PrescriptionIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
  </svg>
);

const TemplateIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
    <rect x="3" y="3" width="18" height="18"/>
    <line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/>
  </svg>
);

const MicIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/>
    <line x1="8" y1="23" x2="16" y2="23"/>
  </svg>
);

const PharmacyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
    <path d="M18.5 2h-13A2.5 2.5 0 0 0 3 4.5v1A2.5 2.5 0 0 0 5.5 8H6v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V8h.5A2.5 2.5 0 0 0 21 5.5v-1A2.5 2.5 0 0 0 18.5 2z"/>
    <line x1="10" y1="12" x2="14" y2="12"/><line x1="12" y1="10" x2="12" y2="14"/>
  </svg>
);

const DocumentIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
  </svg>
);

const BellIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>
);

const FEATURES = [
  {
    icon: <CalendarIcon />,
    title: 'Seamless Appointment Booking',
    desc: 'Manage walk-in and online appointments with a unified calendar. Smart queue management and automated reminders keep operations running smoothly.',
    bullets: ['Walk-in & online bookings', 'Calendar management', 'Appointment reminders', 'Queue management'],
    color: 'cyan',
    premium: false,
  },
  {
    icon: <DoctorIcon />,
    title: 'Doctor Shift Management',
    desc: 'Schedule doctor shifts, manage leaves, and track availability across all branches. Monitor workload and ensure optimal staffing at all times.',
    bullets: ['Shift scheduling', 'Leave management', 'Multi-branch allocation', 'Workload monitoring'],
    color: 'dark',
    premium: false,
  },
  {
    icon: <PrescriptionIcon />,
    title: 'Smart Prescription Creation',
    desc: 'Generate prescriptions in seconds with our intelligent system. Custom formats, digital signatures, and intelligent medication suggestions built in.',
    bullets: ['Fast prescription generation', 'Custom formats', 'Medication suggestions', 'Digital signatures'],
    color: 'cyan',
    premium: false,
  },
  {
    icon: <TemplateIcon />,
    title: 'Prescription Templates',
    desc: 'Save and reuse prescription templates by specialty. One-click prescription generation dramatically reduces consultation time per patient.',
    bullets: ['Save custom templates', 'Specialty-based templates', 'Instant template loading', 'One-click generation'],
    color: 'dark',
    premium: false,
  },
  {
    icon: <MicIcon />,
    title: 'Voice-Based Prescription',
    desc: 'Dictate prescriptions using AI-powered speech-to-text. Reduce typing, speed up consultations, and let doctors focus entirely on patient care.',
    bullets: ['Speech-to-text conversion', 'AI-assisted drafting', 'Natural language support', 'Reduced typing effort'],
    color: 'premium',
    premium: true,
  },
  {
    icon: <PharmacyIcon />,
    title: 'Pharmacy Integration',
    desc: 'Prescriptions link directly to pharmacy dispensing. Real-time inventory tracking, billing integration, and medicine stock alerts keep operations tight.',
    bullets: ['Direct medicine mapping', 'Inventory tracking', 'Stock alerts', 'Billing integration'],
    color: 'cyan',
    premium: false,
  },
  {
    icon: <DocumentIcon />,
    title: 'Patient Document Management',
    desc: 'Centralize all patient documents — lab reports, scans, and medical history — in a secure cloud archive accessible anytime, anywhere.',
    bullets: ['Lab reports storage', 'Scanned uploads', 'Medical history archive', 'Secure cloud access'],
    color: 'dark',
    premium: false,
  },
  {
    icon: <BellIcon />,
    title: 'Follow-up Management',
    desc: 'Never miss a follow-up again. Automated reminders, missed follow-up tracking, and repeat consultation workflows keep patients engaged.',
    bullets: ['Automated reminders', 'Doctor notifications', 'Missed follow-up alerts', 'Repeat consultation flow'],
    color: 'cyan',
    premium: false,
  },
];

const FeatureCard = ({ feature, index, isVisible }) => (
  <div className={`feature-card feature-card--${feature.color} reveal delay-${(index % 4) + 1}${isVisible ? ' visible' : ''}`}>
    {feature.premium && (
      <div className="feature-card__premium-badge">Premium Feature</div>
    )}
    <div className={`feature-card__icon-wrap feature-card__icon-wrap--${feature.color}`}>
      {feature.icon}
    </div>
    <h3 className="feature-card__title">{feature.title}</h3>
    <p className="feature-card__desc">{feature.desc}</p>
    <ul className="feature-card__bullets">
      {feature.bullets.map((b) => (
        <li key={b}>
          <span className="bullet-check">→</span>
          {b}
        </li>
      ))}
    </ul>
  </div>
);

const CoreFeatures = () => {
  const [ref, isVisible] = useScrollReveal(0.05);

  return (
    <section className="core-features section" id="features" ref={ref}>
      <div className="container">
        <div className={`section-header centered reveal${isVisible ? ' visible' : ''}`}>
          <span className="section-tag">Core Features</span>
          <h2 className="section-title">Everything Your Hospital Needs</h2>
          <p className="section-subtitle centered">
            From first appointment to final follow-up, Tecnivorns HMS handles every clinical and administrative workflow in one integrated platform.
          </p>
        </div>

        <div className="features-grid">
          {FEATURES.map((feature, i) => (
            <FeatureCard key={feature.title} feature={feature} index={i} isVisible={isVisible} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default CoreFeatures;
